import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { NavbarComponent } from '../navbar/navbar.component';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { DateTimeHelperService } from '../shared/date-time-helper.service';

interface NotificationItem {
  id: number | string;
  passportId?: number | string;
  loadNumber: string;
  commenterName: string;
  message: string;
  createdAt: string;
}

@Component({
  selector: 'app-comments-notifications',
  standalone: true,
  imports: [CommonModule, NavbarComponent],
  providers: [DatePipe],
  templateUrl: './comments-notifications.component.html',
  styleUrl: './comments-notifications.component.css'
})
export class CommentsNotificationsComponent implements OnInit {
  title = 'Comments Notifications';
  items = signal<NotificationItem[]>([]);
  isLoading = signal(false);

  private readonly http = inject(HttpClient);
  readonly router = inject(Router);
  private readonly commentsNotification = inject(CommentsNotificationService);
  private readonly detailsStore = inject(PassportDetailsStore);
  private readonly datePipe = inject(DatePipe);
  private readonly commentsPanelService = inject(CommentsPanelService);
  private readonly dateTimeHelper = inject(DateTimeHelperService);

  ngOnInit(): void {
    // Load notifications from service (includes mock data)
    void this.load();
    // Refresh service only after load to ensure mock data is preserved
    setTimeout(() => {
      void this.commentsNotification.refresh();
    }, 200);
  }

  async load(): Promise<void> {
    this.isLoading.set(true);
    try {
      // First, try to get from the service (which includes mock data)
      const serviceComments = this.commentsNotification.getComments();
      
      if (serviceComments && serviceComments.length > 0) {
        console.log('📦 Loading notifications from service:', serviceComments.length);
        const mapped: NotificationItem[] = serviceComments.map((c: any, idx: number) => {
          const createdAt = c.createdOn || c.createdAt || new Date().toISOString();
          
          return {
            id: c.commentID ?? idx,
            passportId: c.passportID ?? c.PassportID ?? null,
            loadNumber: c.loadNumber ?? c.passportID ?? '',
            commenterName: c.employeeName || c.username || c.userName || 'User',
            message: c.commentText || c.message || '',
            createdAt: createdAt,
          };
        });
        this.items.set(mapped);
        console.log('✅ Mapped notifications:', mapped);
      } else {
        // Only try API if we have authentication data
        const userID = localStorage.getItem('userID');
        const token = localStorage.getItem('accessToken');
        const tokenType = localStorage.getItem('tokenType') || 'Bearer';
        
        if (!userID || !token) {
          console.warn('No service comments and missing authentication data');
          this.items.set([]);
          return;
        }
        // Fallback to API if service has no data
        const fromDate = new Date();
        fromDate.setHours(fromDate.getHours() - 24);
        const apiUrl = `${environment.apiBaseUrl}/api/v1/passports/comments`;
        const headers = { 'Authorization': `${tokenType} ${token}`, 'Content-Type': 'application/json' };
        const body = {
          UserId: Number.parseInt(userID, 10),
          FromDate: fromDate.toISOString()
        };
        
        const resp = await firstValueFrom(this.http.post<any[]>(apiUrl, body, { headers }));
        const mapped: NotificationItem[] = (resp || []).map((c: any, idx: number) => {
          const createdAt = c.createdOn || c.createdAt || new Date().toISOString();
          
          return {
            id: c.commentID ?? idx,
            passportId: c.passportID ?? c.PassportID ?? null,
            loadNumber: c.loadNumber ?? c.passportID ?? '',
            commenterName: c.employeeName || c.username || c.userName || 'User',
            message: c.commentText || c.message || '',
            createdAt: createdAt,
          };
        });
        this.items.set(mapped);
      }
    } catch (e) {
      console.warn('Failed to load comments notifications:', e);
      this.items.set([]);
    } finally {
      this.isLoading.set(false);
    }
  }

  goBack(): void { history.back(); }

  initials(name: string | null | undefined): string {
    const safe = (name || '').trim();
    if (!safe) return '';
    const parts = safe.split(/\s+/).filter(Boolean);
    const first = parts[0]?.charAt(0) ?? '';
    const last = parts.length > 1 ? parts.at(-1)?.charAt(0) ?? '' : '';
    return (first + last).toUpperCase();
  }

  formatAgo(iso: string | null | undefined): string {
    if (!iso) return '0 mins ago';
    
    const originalTimestamp = iso.trim();
    const now = Date.now();

    // If no timezone info present, treat timestamp as UTC (backend sends UTC without 'Z')
    const hasTimezone = /(?:Z|[+-]\d{2}:\d{2})$/.test(originalTimestamp);
    const parsedTime = hasTimezone
      ? new Date(originalTimestamp).getTime()
      : new Date(originalTimestamp + 'Z').getTime();

    let diffMs = now - parsedTime;
    
    if (!Number.isFinite(diffMs) || diffMs < 0) diffMs = 0;
    const totalMinutes = Math.floor(diffMs / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const mins = totalMinutes % 60;
    const parts: string[] = [];
    if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
    if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    if (mins || parts.length === 0) parts.push(`${mins} min${mins === 1 ? '' : 's'}`);
    return parts.join(' ') + ' ago';
  }
  async openNotification(n: NotificationItem): Promise<void> {
    const passportId = (n.passportId != null && n.passportId !== '') ? String(n.passportId) : '';
    if (!passportId) {
      console.warn('No passportId on notification item, cannot navigate to comments.', n);
      return;
    }
    
    // Get passport details from store
    const details = this.detailsStore.get(passportId);
    
    // Build header with available data from store
    const header = {
      loadNumber: n.loadNumber || details?.loadNumber || passportId,
      variety: details?.variety || '',
      haulier: details?.haulier || '',
      scheduledText: details?.dueDateTime 
        ? (this.dateTimeHelper.formatScheduledText(this.dateTimeHelper.ensureUtcTimestamp(details.dueDateTime)) || '')
        : ''
    };
    
    // Check if desktop view (window width >= 768px)
    const isDesktop = globalThis.window !== undefined && globalThis.window.innerWidth >= 768;
    
    if (isDesktop) {
      // Use service to open comments panel for desktop
      this.commentsPanelService.openCommentsPanel(passportId, header);
    } else {
      // Navigate to comments page for mobile
      void this.router.navigate(['/comments'], {
        state: {
          passportId,
          header
        }
      });
    }
  }
}


